/* assets/product-action-panel.js — PRODUCT-ACTION-PANEL-RUNTIME-11
 * Owner: product-action DOM adapter. Four locked family/light-professional products
 * use dedicated inputs but keep GCityExecutor + Shopify App Proxy as execution authority.
 * C1 Property Check is a free read-only valuation-precheck surface. It calls the canonical
 * canonical public Spatial Entry API once per explicit submit, never creates checkout/GFun/delivery jobs,
 * and renders only place verification, data readiness, existing/missing evidence, sourced market
 * context, preliminary readiness rules and next checks — never an opinion of value.
 * C1 structural DOM belongs to sections/product-action-panel.liquid; this runtime binds behavior and data only.
 * Paid-tool purchase uses Shopify cart/checkout only; entitlement truth remains server-side.
 * No client-side entitlement/GFun truth, no workers.dev URL, no fabricated result.
 */
(function productActionPanelRuntime(window, document) {
  'use strict';

  var ROOT_SELECTOR = '[data-module="product-action-panel"]';
  var ACTION_SELECTOR = '[data-gcity-execute]';
  var CONSENT_VERSION = '2026-08-family-private-v1';
  var APP_PROXY_CORE_PREFIX = '/apps/gcity-api/core';
  var C1_SPATIAL_CONTRACT = 'littleg_spatial_intent_v1';
  var C1_CONSUMER_JOB = 'understand_value';
  var C1_ADDRESS_PLACEHOLDERS = Object.freeze([
    '例如：海星閣 10 太古城道',
    '例如：中環廣場 18 Harbour Road',
    '輸入大廈名稱 + 街道 / 門牌會更容易核實'
  ]);
  var C1_REQUIRED_INFORMATION = Object.freeze([
    { key: 'area_survey', label: '樓面面積來源 / 測繪資料' },
    { key: 'tenancy_confirmation', label: '現時租用 / 空置狀態' },
    { key: 'floor_confirmation', label: '樓層 / 單位確認' }
  ]);
  var C1_INFORMATION_LABELS = Object.freeze({
    ownership: '權屬 / 業權資料',
    area_survey: '面積 / 測繪資料',
    floor_plan: '平面圖 / 建築圖則',
    floor_confirmation: '樓層 / 單位確認',
    tenancy_confirmation: '現時租用 / 空置狀態',
    lease: '租約',
    site_photos: '現場照片',
    other: '其他資料'
  });
  var C1_PROPERTY_TYPE_LABELS = Object.freeze({
    residential: '住宅', industrial: '廠房 / 倉庫', retail: '商舖', office: '寫字樓', land: '土地', other: '其他'
  });
  var C1_INTENT_LABELS = Object.freeze({
    sale: '準備出售', purchase: '購買前確認', mortgage: '銀行按揭 / 抵押續期', tax: '稅務相關',
    financial_reporting: '公司賬務 / 財務報告', asset_division: '資產分割', planning: '了解是否值得進一步評估', other: '其他'
  });
  var LOCKED = Object.freeze({
    'bb-voice-growth-diary': { id: '15593561161809', endpoint: '/v1/family/voice-diary/entries', role: 'voice', surface: 'dashboard' },
    'tutoring-roi-analyzer': { id: '15612816326737', endpoint: '/v1/family/tutoring-value-check', role: 'tutoring', surface: 'panel', requires_entitlement: true },
    'property-rights-verification-workflow': { id: '15691574378577', endpoint: '/v1/property/rights/precheck', role: 'rights', surface: 'dashboard' }
  });

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function bool(value) { return clean(value).toLowerCase() === 'true'; }
  function hasOwn(object, key) { return Object.prototype.hasOwnProperty.call(object || {}, key); }
  function lockedConfig(root) { return LOCKED[clean(root && root.dataset && root.dataset.productHandle)] || null; }
  function make(tag, attrs, text) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (key) {
      if (key === 'className') node.className = attrs[key];
      else if (key === 'value') node.value = attrs[key];
      else if (key === 'hidden') node.hidden = Boolean(attrs[key]);
      else node.setAttribute(key, attrs[key]);
    });
    if (text != null) node.textContent = text;
    return node;
  }
  function field(labelText, input) {
    var label = make('label', { className: 'field product-action-panel__runtime-field' });
    label.appendChild(make('span', { className: 'product-action-panel__runtime-label' }, labelText));
    label.appendChild(input);
    return label;
  }
  function button(label, actionName) {
    return make('button', { type: 'button', className: 'product-action-panel__button', 'data-runtime-action': actionName }, label);
  }
  function statusNode(root) {
    var node = root.querySelector('[data-product-action-status]');
    if (node) return node;
    node = make('p', { className: 'product-action-panel__runtime-status', 'data-product-action-status': '', role: 'status', 'aria-live': 'polite' });
    root.appendChild(node);
    return node;
  }
  function resultNode(root) {
    var node = root.querySelector('[data-product-runtime-result]');
    if (node) return node;
    node = make('pre', { className: 'product-action-panel__runtime-result', 'data-product-runtime-result': '', tabindex: '0', hidden: true });
    root.appendChild(node);
    return node;
  }
  function friendlyError(root, error) {
    var message = clean(error && error.message);
    if (!root || !root.matches('[data-paid-tool]')) return message || 'The product action failed.';
    if (/entitlement_required|active entitlement|product entitlement|payment required|\b402\b/i.test(message)) {
      return '尚未有可用的完整分析權益。先購買一次完整分析，付款完成後回到此頁再生成。';
    }
    if (/auth|login|principal|unauthori[sz]ed|\b401\b/i.test(message)) {
      return '請先登入購買時使用的 LittleG 帳戶，再繼續。';
    }
    if (/timeout|timed out/i.test(message)) return '連線逾時，未確認完成。請再試一次。';
    return message || '暫時未能完成，請再試一次。';
  }
  function setState(root, control, state, message) {
    root.setAttribute('data-execution-state', state);
    var status = statusNode(root);
    status.textContent = clean(message);
    status.hidden = !status.textContent;
    if (control) {
      control.disabled = state === 'executing';
      control.setAttribute('aria-busy', state === 'executing' ? 'true' : 'false');
    }
  }
  function show(root, value) {
    var node = resultNode(root);
    node.textContent = typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2);
    node.hidden = false;
  }
  function workspace(root, title, Note) {
    var existing = root.querySelector('[data-product-runtime-workspace]');
    if (existing) return existing;
    var box = make('div', { className: 'product-action-panel__runtime-workspace', 'data-product-runtime-workspace': '' });
    box.appendChild(make('h3', {}, title));
    if (Note) box.appendChild(make('p', { className: 'product-action-panel__runtime-Note' }, Note));
    var actions = root.querySelector('.product-action-panel__actions');
    if (actions && actions.parentNode) actions.parentNode.insertBefore(box, actions);
    else root.appendChild(box);
    return box;
  }
  function consent(labelText) {
    var label = make('label', { className: 'product-action-panel__runtime-consent' });
    var input = make('input', { type: 'checkbox', 'data-runtime-consent': '' });
    label.appendChild(input);
    label.appendChild(make('span', {}, labelText));
    return { node: label, input: input };
  }
  function executorReady() {
    var executor = window.GCityExecutor;
    return Boolean(executor && executor.__isFallbackStub !== true && executor.isReady !== false && typeof executor.execute === 'function');
  }
  function context(root, endpoint, data, overrides) {
    var locked = lockedConfig(root) || {};
    overrides = overrides || {};
    var requiresEntitlement = hasOwn(overrides, 'requires_entitlement') ? Boolean(overrides.requires_entitlement) : (locked.requires_entitlement === true || bool(root.dataset.requiresEntitlement));
    var requiresGfun = hasOwn(overrides, 'requires_gfun') ? Boolean(overrides.requires_gfun) : (locked.requires_gfun === true || bool(root.dataset.requiresGfun));
    return {
      product_handle: clean(root.dataset.productHandle),
      product_id: locked.id || clean(root.dataset.productId),
      api_family: 'gcity-core-api',
      canonical_endpoint: endpoint || locked.endpoint || clean(root.dataset.canonicalEndpoint),
      delivery_type: overrides.delivery_type || clean(root.dataset.deliveryType) || 'tool',
      delivery_method: overrides.delivery_method || clean(root.dataset.deliveryMethod) || 'instant',
      engine_type: overrides.engine_type || 'decision_engine',
      result_surface: overrides.result_surface || locked.surface || clean(root.dataset.resultSurface) || 'panel',
      requires_entitlement: requiresEntitlement,
      requires_gfun: requiresGfun,
      data: data || {}
    };
  }
  function errorText(result) {
    var error = result && result.error;
    if (error && typeof error === 'object') return clean(error.message || error.code);
    return clean(error || result && (result.reason || result.message)) || 'The product action failed.';
  }
  function executeJson(root, endpoint, data, overrides) {
    if (!executorReady()) return Promise.reject(new Error('The product execution service is not ready.'));
    var executionOptions = overrides && overrides.server_authoritative_entitlement === true ? { skipGate: true } : undefined;
    return Promise.resolve(window.GCityExecutor.execute(context(root, endpoint, data, overrides), executionOptions)).then(function (result) {
      if (!result || result.ok === false || result.success === false || result.blocked === true) throw new Error(errorText(result));
      return result.data && typeof result.data === 'object' ? result.data : result;
    });
  }
  function executeMultipart(root, endpoint, data, overrides) {
    if (!executorReady() || typeof window.GCityExecutor.executeMultipart !== 'function') return Promise.reject(new Error('Private file upload is not ready.'));
    return Promise.resolve(window.GCityExecutor.executeMultipart(context(root, endpoint, data, overrides))).then(function (result) {
      if (!result || result.ok === false || result.success === false || result.blocked === true) throw new Error(errorText(result));
      return result.data && typeof result.data === 'object' ? result.data : result;
    });
  }
  function busy(root, control, task, renderer) {
    setState(root, control, 'executing', root.matches('[data-paid-tool]') ? '正在處理…' : 'Working…');
    return Promise.resolve().then(task).then(function (value) {
      setState(root, control, 'complete', root.matches('[data-paid-tool]') ? '結果已更新。' : 'Verified runtime response received.');
      if (typeof renderer === 'function') renderer(root, value);
      else show(root, value);
      return value;
    }).catch(function (error) {
      setState(root, control, 'failed', friendlyError(root, error));
      throw error;
    });
  }
  function appProxyDownload(endpoint, filename) {
    if (!String(endpoint || '').startsWith('/v1/')) return;
    var link = make('a', { href: APP_PROXY_CORE_PREFIX + endpoint, download: filename || 'download' });
    link.hidden = true;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  function shopRoot(root) {
    var value = clean(root.dataset.shopRoot || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
    if (!value.startsWith('/')) value = '/' + value;
    return value.endsWith('/') ? value : value + '/';
  }
  function preparePaidTutoringShell(root) {
    if (!root.matches('[data-paid-tool]')) return;
    var inner = root.querySelector('.product-action-panel__inner');
    if (!inner || inner.getAttribute('data-paid-tool-shell') === 'ready') return;
    inner.setAttribute('data-paid-tool-shell', 'ready');
    inner.textContent = '';
    var header = make('header', { className: 'product-action-panel__paid-hero' });
    var copy = make('div', { className: 'product-action-panel__paid-copy' });
    copy.appendChild(make('p', { className: 'product-action-panel__eyebrow' }, 'LITTLEG · DECISION CHECK'));
    copy.appendChild(make('h1', {}, '這項補習，值不值得繼續？'));
    copy.appendChild(make('p', { className: 'product-action-panel__paid-lead' }, '用成本、時間、目標清晰度與目前證據，做一次即時價值檢查。'));
    var chips = make('div', { className: 'product-action-panel__paid-chips', 'aria-label': '交付摘要' });
    ['一次購買', '即時結果', '私人保存'].forEach(function (label) { chips.appendChild(make('span', {}, label)); });
    copy.appendChild(chips);
    var offer = make('aside', { className: 'product-action-panel__paid-offer', 'aria-label': '購買完整分析' });
    offer.appendChild(make('span', {}, '完整分析'));
    offer.appendChild(make('strong', {}, clean(root.dataset.priceLabel) || '價格以 Shopify 為準'));
    offer.appendChild(make('p', {}, '付款後取得一次完整分析權益；是否可使用由伺服器最終確認。'));
    header.appendChild(copy); header.appendChild(offer); inner.appendChild(header);
    var actions = make('div', { className: 'product-action-panel__actions product-action-panel__paid-actions' });
    if (!bool(root.dataset.loggedIn)) {
      actions.appendChild(make('a', { className: 'product-action-panel__button product-action-panel__button--primary', href: clean(root.dataset.loginUrl) || '/account/login', 'data-no-legacy-normalize': 'true' }, '登入後開始'));
      actions.appendChild(make('p', { className: 'product-action-panel__paid-note' }, '購買前先登入，確保付款後的權益能回到同一帳戶。'));
    } else if (!bool(root.dataset.variantAvailable)) {
      var unavailable = button('暫時未能購買', 'paid-tool-unavailable'); unavailable.disabled = true; actions.appendChild(unavailable);
    } else {
      var buy = button('購買一次完整分析 · ' + (clean(root.dataset.priceLabel) || 'Shopify 價格'), 'paid-tool-buy');
      buy.classList.add('product-action-panel__button--primary'); buy.setAttribute('data-paid-tool-buy', ''); actions.appendChild(buy);
      actions.appendChild(make('p', { className: 'product-action-panel__paid-note' }, '已有未使用的完整分析權益？可直接在上方輸入資料並生成；前端不自行判斷權益。'));
    }
    inner.appendChild(actions);
  }
  function bindPaidToolPurchase(root) {
    var control = root.querySelector('[data-paid-tool-buy]');
    if (!control) return;
    control.addEventListener('click', function () {
      if (control.disabled || control.getAttribute('aria-busy') === 'true') return;
      var variantId = Number(clean(root.dataset.variantId));
      if (!variantId) return setState(root, control, 'blocked', '目前未能確認可購買的產品版本。');
      var base = shopRoot(root);
      setState(root, control, 'executing', '正在前往安全結帳…');
      window.fetch(base + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      }).then(function (response) {
        if (!response.ok) throw new Error('checkout_unavailable');
        return response.json();
      }).then(function () {
        window.location.assign('/checkout');
      }).catch(function () {
        setState(root, control, 'failed', '暫時未能開啟結帳，請再試一次。');
      });
    });
  }
  function hkd(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) return '—';
    try { return new Intl.NumberFormat('zh-HK', { style: 'currency', currency: 'HKD', maximumFractionDigits: 0 }).format(number); }
    catch (_) { return 'HK$' + Math.round(number); }
  }
  function metric(label, value) {
    var card = make('article', { className: 'product-action-panel__metric' });
    card.appendChild(make('span', {}, label));
    card.appendChild(make('strong', {}, value));
    return card;
  }
  function tutoringRecommendation(code) {
    var labels = {
      clarify_goal_first: '先把學習目標定清楚',
      collect_baseline_first: '先建立基準，再判斷值不值得',
      high_cost_review: '成本偏高，需要用實際證據覆核',
      review_with_evidence: '可以繼續，但要按期用證據覆核'
    };
    return labels[clean(code)] || '先用成本、時間與證據做下一次覆核';
  }
  function renderTutoringResult(root, data) {
    var surface = root.querySelector('[data-tutoring-result]');
    if (!surface) return;
    var result = data && data.result && typeof data.result === 'object' ? data.result : data || {};
    var paid = clean(data && data.access_tier) === 'paid' || clean(result.access_tier) === 'paid';
    surface.textContent = '';
    surface.hidden = false;
    surface.setAttribute('data-result-tier', paid ? 'paid' : 'preview');
    var head = make('header', { className: 'product-action-panel__result-head' });
    head.appendChild(make('span', { className: 'product-action-panel__result-kicker' }, paid ? '完整分析' : '免費預覽'));
    head.appendChild(make('h4', {}, tutoringRecommendation(result.recommendation_code)));
    head.appendChild(make('p', {}, paid ? '完整結果已由伺服器確認並保存到你的帳戶。' : '先看成本與時間；完整分析會再解鎖目標清晰度和證據完整度。'));
    surface.appendChild(head);
    var metrics = make('div', { className: 'product-action-panel__metrics' });
    metrics.appendChild(metric('每月費用', hkd(result.monthly_cost_hkd)));
    metrics.appendChild(metric('每月補習時間', Number.isFinite(Number(result.monthly_hours)) ? Number(result.monthly_hours).toFixed(1) + ' 小時' : '—'));
    metrics.appendChild(metric('每小時成本', hkd(result.cost_per_hour_hkd)));
    if (paid) {
      metrics.appendChild(metric('目標清晰度', Number.isFinite(Number(result.goal_clarity_score)) ? result.goal_clarity_score + ' / 5' : '—'));
      metrics.appendChild(metric('證據完整度', Number.isFinite(Number(result.evidence_score)) ? result.evidence_score + ' / 5' : '—'));
    }
    metrics.appendChild(metric('下次覆核', clean(result.review_on) || '—'));
    surface.appendChild(metrics);
    if (clean(result.Note)) surface.appendChild(make('p', { className: 'product-action-panel__result-Note' }, result.Note));
    if (!paid) surface.appendChild(make('p', { className: 'product-action-panel__result-lock' }, '完整分析需要一次有效購買權益；前端不自行判斷權益，以伺服器回應為準。'));
    try { surface.focus({ preventScroll: true }); } catch (_) { surface.focus(); }
    surface.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
  }

  function bindVoice(root) {
    var box = workspace(root, 'BB Voice Growth Diary', 'Private family storage. The first 10 entries are the free-history Note; Family Plus is enforced by the backend.');
    var transcript = make('textarea', { rows: '4', placeholder: 'What happened today?' });
    var permission = consent('I agree to store this private family memory for the Voice Diary workflow.');
    var save = button('Save entry', 'voice-save');
    var audio = make('input', { type: 'file', accept: 'audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,audio/webm' });
    box.appendChild(field('Diary note / transcript', transcript));
    box.appendChild(field('Optional audio recording', audio));
    box.appendChild(permission.node); box.appendChild(save);
    save.addEventListener('click', function () {
      if (!permission.input.checked) return setState(root, save, 'blocked', 'Consent is required before private family storage.');
      if (!clean(transcript.value) && !(audio.files && audio.files[0])) return setState(root, save, 'blocked', 'Add a note or audio recording first.');
      busy(root, save, function () {
        return executeJson(root, '/v1/family/voice-diary/entries', {
          transcript_text: clean(transcript.value), occurred_at: new Date().toISOString(), consent_version: CONSENT_VERSION
        }).then(function (created) {
          var file = audio.files && audio.files[0];
          if (!file || !created.entry_id) return created;
          return executeMultipart(root, '/v1/family/voice-diary/entries/' + encodeURIComponent(created.entry_id) + '/audio', { file: file }, { result_surface: 'dashboard' })
            .then(function (upload) { return { entry: created, audio: upload }; });
        });
      }).then(function () { transcript.value = ''; audio.value = ''; }).catch(function () {});
    });
  }

  function bindMemory(root) {
    var box = workspace(root, 'Family Memory Book', 'Preview uses only memories owned by this family account. Paid generation creates private digital HTML; PDF is not connected.');
    var title = make('input', { type: 'text', autocomplete: 'off', placeholder: 'Family Memory Book' });
    var note = make('textarea', { rows: '4', placeholder: 'Parent note' });
    var sources = make('div', { className: 'product-action-panel__runtime-source-list', 'data-memory-source-list': '' });
    var permission = consent('I agree to use the selected private family memories for this book.');
    var load = button('Load my memories', 'memory-load');
    var preview = button('Create preview', 'memory-preview');
    var generate = button('Generate paid digital book', 'memory-generate');
    var download = button('Download digital book', 'memory-download');
    download.hidden = true;
    box.appendChild(field('Book title', title)); box.appendChild(field('Parent note', note)); box.appendChild(load); box.appendChild(sources);
    box.appendChild(permission.node); box.appendChild(preview); box.appendChild(generate); box.appendChild(download);
    var projectId = ''; var downloadEndpoint = '';

    function selected() {
      return Array.prototype.slice.call(sources.querySelectorAll('input[type="checkbox"]:checked')).map(function (node) { return clean(node.value); }).filter(Boolean);
    }
    load.addEventListener('click', function () {
      busy(root, load, function () { return executeJson(root, '/v1/family/memory-book/source-items', {}); }).then(function (data) {
        sources.textContent = '';
        (data.items || []).forEach(function (item) {
          var label = make('label', { className: 'product-action-panel__runtime-memory-item' });
          label.appendChild(make('input', { type: 'checkbox', value: clean(item.item_id) }));
          label.appendChild(make('span', {}, [clean(item.occurred_at), clean(item.transcript_text) || clean(item.title) || 'Family memory'].filter(Boolean).join(' · ').slice(0, 220)));
          sources.appendChild(label);
        });
      }).catch(function () {});
    });
    preview.addEventListener('click', function () {
      if (!permission.input.checked) return setState(root, preview, 'blocked', 'Consent is required before creating a private memory project.');
      if (!selected().length && !clean(note.value)) return setState(root, preview, 'blocked', 'Select at least one owned memory or add parent text.');
      busy(root, preview, function () {
        return executeJson(root, '/v1/family/memory-book/projects', {
          title: clean(title.value) || 'Family Memory Book', consent_version: CONSENT_VERSION,
          chapters: [{ title: clean(title.value) || 'Family Memories', item_ids: selected(), parent_text: clean(note.value) }]
        }).then(function (created) {
          projectId = clean(created.project_id);
          if (!projectId) throw new Error('Memory project was not created.');
          return executeJson(root, '/v1/family/memory-book/' + encodeURIComponent(projectId) + '/preview', {});
        });
      }).catch(function () {});
    });
    generate.addEventListener('click', function () {
      if (!projectId) return setState(root, generate, 'blocked', 'Create a preview first.');
      busy(root, generate, function () {
        return executeJson(root, '/v1/family/memory-book/' + encodeURIComponent(projectId) + '/generate', {}, { delivery_type: 'report', delivery_method: 'email', result_surface: 'download' });
      }).then(function (data) { downloadEndpoint = clean(data.download_endpoint); download.hidden = !downloadEndpoint; }).catch(function () {});
    });
    download.addEventListener('click', function () { appProxyDownload(downloadEndpoint); });
  }

  function bindTutoring(root) {
    if (root.matches('[data-paid-tool]') && !bool(root.dataset.loggedIn)) return;
    var box = workspace(root, '補習價值檢查', '比較成本、時間、目標清晰度和目前證據；不預測或保證學業進步。');
    var goal = make('textarea', { rows: '3', placeholder: '例如：6 週後數學測驗由 70 分提升到 80 分' });
    var cost = make('input', { type: 'number', min: '0', step: '1', inputmode: 'decimal', placeholder: '例如：5200' });
    var minutes = make('input', { type: 'number', min: '1', step: '1', inputmode: 'numeric', placeholder: '例如：240' });
    var evidence = make('textarea', { rows: '4', placeholder: '最近測驗、功課、錯題或家長觀察' });
    var permission = consent('我同意只在生成已購買的完整分析時，保存本次輸入與結果。');
    var preview = button('先看免費預覽', 'tutoring-preview');
    var full = button('生成我的完整分析', 'tutoring-full');
    full.classList.add('product-action-panel__button--primary');
    var exportButton = button('建立 English PDF', 'tutoring-export');
    var download = button('下載 English PDF', 'tutoring-download');
    exportButton.hidden = true; download.hidden = true;
    box.appendChild(field('你想改善甚麼？', goal));
    box.appendChild(field('每月補習費用（HKD）', cost));
    box.appendChild(field('每週補習時間（分鐘）', minutes));
    box.appendChild(field('目前有甚麼實際證據？', evidence));
    box.appendChild(permission.node); box.appendChild(preview); box.appendChild(full); box.appendChild(exportButton); box.appendChild(download);
    var resultSurface = make('section', { className: 'product-action-panel__tutoring-result', 'data-tutoring-result': '', tabindex: '-1', 'aria-live': 'polite', hidden: true });
    box.appendChild(resultSurface);
    var resultId = ''; var downloadEndpoint = '';
    function payload(mode) {
      var data = { goal: clean(goal.value), monthly_cost_hkd: Number(cost.value), weekly_minutes: Number(minutes.value), current_evidence: clean(evidence.value), mode: mode };
      if (mode === 'full') data.consent_version = CONSENT_VERSION;
      return data;
    }
    preview.addEventListener('click', function () {
      busy(root, preview, function () {
        return executeJson(root, '/v1/family/tutoring-value-check', payload('preview'), { requires_entitlement: false, requires_gfun: false });
      }, renderTutoringResult).catch(function () {});
    });
    full.addEventListener('click', function () {
      if (!permission.input.checked) return setState(root, full, 'blocked', '生成完整分析前，請先確認資料用途。');
      busy(root, full, function () {
        return executeJson(root, '/v1/family/tutoring-value-check', payload('full'), { requires_entitlement: true, requires_gfun: false, server_authoritative_entitlement: true });
      }, renderTutoringResult).then(function (data) {
        resultId = clean(data.result_id); exportButton.hidden = !resultId;
        if (resultId) root.setAttribute('data-paid-tool-result', 'full');
      }).catch(function () {});
    });
    exportButton.addEventListener('click', function () {
      if (!resultId) return;
      busy(root, exportButton, function () {
        return executeJson(root, '/v1/family/tutoring-value-check/' + encodeURIComponent(resultId) + '/export', {}, { delivery_type: 'report', delivery_method: 'pdf', result_surface: 'download', requires_entitlement: true, requires_gfun: false, server_authoritative_entitlement: true });
      }, function () {}).then(function (data) {
        downloadEndpoint = clean(data.download_endpoint); download.hidden = !downloadEndpoint;
        setState(root, exportButton, 'complete', downloadEndpoint ? 'English PDF 已準備好。' : '未收到可下載檔案。');
      }).catch(function () {});
    });
    download.addEventListener('click', function () { appProxyDownload(downloadEndpoint, 'tutoring-value-check.pdf'); });
  }

  function bindRights(root) {
    var box = workspace(root, 'Property Rights Pre-check', 'Public address data and entered Lot/PRN/CSUID values do not prove ownership, incumbrances, memorials or clear title. LPPUN parcel lookup and OCR are not connected in this runtime.');
    var address = make('input', { type: 'text', autocomplete: 'street-address' });
    var lot = make('input', { type: 'text', autocomplete: 'off' });
    var prn = make('input', { type: 'text', autocomplete: 'off' });
    var csuid = make('input', { type: 'text', autocomplete: 'off' });
    var precheck = button('Run identity pre-check', 'rights-precheck');
    var file = make('input', { type: 'file', accept: 'application/pdf,image/jpeg,image/png' });
    var permission = consent('I agree to store this supporting document privately for my property-rights case.');
    var upload = button('Upload private supporting document', 'rights-upload');
    var tier = make('select');
    tier.appendChild(make('option', { value: 'rights-evidence-review' }, 'Rights Evidence Review'));
    tier.appendChild(make('option', { value: 'human-desk-review' }, 'Human Desk Review'));
    var review = button('Request paid review', 'rights-review');
    box.appendChild(field('Address', address)); box.appendChild(field('Lot number', lot)); box.appendChild(field('PRN', prn)); box.appendChild(field('CSUID', csuid)); box.appendChild(precheck);
    box.appendChild(field('Supporting PDF/JPEG/PNG', file)); box.appendChild(permission.node); box.appendChild(upload); box.appendChild(field('Review tier', tier)); box.appendChild(review);
    var caseId = '';
    precheck.addEventListener('click', function () {
      busy(root, precheck, function () {
        return executeJson(root, '/v1/property/rights/precheck', { address: clean(address.value), lot_number: clean(lot.value), prn: clean(prn.value), csuid: clean(csuid.value) }, { delivery_type: 'service', result_surface: 'dashboard' });
      }).then(function (data) { caseId = clean(data.case_id); }).catch(function () {});
    });
    upload.addEventListener('click', function () {
      if (!caseId) return setState(root, upload, 'blocked', 'Run the identity pre-check first.');
      if (!permission.input.checked) return setState(root, upload, 'blocked', 'Consent is required before private document storage.');
      var selected = file.files && file.files[0];
      if (!selected) return setState(root, upload, 'blocked', 'Choose a PDF, JPEG or PNG first.');
      busy(root, upload, function () {
        return executeMultipart(root, '/v1/property/rights/' + encodeURIComponent(caseId) + '/documents', {
          file: selected, filename: selected.name, document_type: 'supporting_document', consent_version: CONSENT_VERSION
        }, { delivery_type: 'service', result_surface: 'dashboard' });
      }).catch(function () {});
    });
    review.addEventListener('click', function () {
      if (!caseId) return setState(root, review, 'blocked', 'Run the identity pre-check first.');
      busy(root, review, function () {
        return executeJson(root, '/v1/property/rights/' + encodeURIComponent(caseId) + '/review', { tier: clean(tier.value) }, { delivery_type: 'service', delivery_method: 'human', result_surface: 'dashboard' });
      }).catch(function () {});
    });
  }

  function c1SelectedInformation(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-c1-info]:checked')).map(function (node) {
      return clean(node.value);
    }).filter(Boolean);
  }

  function c1Intake(root) {
    var location = root.querySelector('[data-c1-location]');
    var propertyType = root.querySelector('[data-c1-property-type]');
    var intent = root.querySelector('[data-c1-intent]');
    return {
      location: clean(location && location.value),
      property_type: clean(propertyType && propertyType.value),
      intent: clean(intent && intent.value),
      available_information: c1SelectedInformation(root)
    };
  }

  function validateC1Intake(root, control) {
    var data = c1Intake(root);
    if (!data.location) {
      setState(root, control, 'blocked', '請先輸入物業或地址。');
      var location = root.querySelector('[data-c1-location]');
      if (location) location.focus();
      return null;
    }
    return data;
  }

  function c1BindAddressPrompt(root) {
    var input = root.querySelector('[data-c1-location]');
    if (!input || input.dataset.c1AddressPrompt === 'ready') return;
    input.dataset.c1AddressPrompt = 'ready';
    input.setAttribute('enterkeyhint', 'search');
    input.setAttribute('inputmode', 'text');
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;
    function update() {
      if (document.activeElement === input || clean(input.value)) return;
      input.placeholder = C1_ADDRESS_PLACEHOLDERS[index % C1_ADDRESS_PLACEHOLDERS.length];
      index += 1;
    }
    update();
    if (!reduceMotion) input.__c1PlaceholderTimer = window.setInterval(update, 2800);
  }

  function c1Readiness(intake, placeVerified) {
    var available = new Set(intake.available_information || []);
    var missing = C1_REQUIRED_INFORMATION.filter(function (item) { return !available.has(item.key); });
    var ready = (intake.available_information || []).map(function (key) {
      return { key: key, label: C1_INFORMATION_LABELS[key] || key };
    });
    var passedSignals = placeVerified ? 1 : 0;
    C1_REQUIRED_INFORMATION.forEach(function (item) { if (available.has(item.key)) passedSignals += 1; });
    var score = Math.round((passedSignals / (C1_REQUIRED_INFORMATION.length + 1)) * 100);
    return { missing: missing, ready: ready, score: score, available: available };
  }

  function c1RequestId() {
    var token = '';
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') token = window.crypto.randomUUID();
    } catch (_) {}
    if (!token) token = String(Date.now()) + '-' + Math.random().toString(36).slice(2, 12);
    return ('REQ-c1-free-' + token).replace(/[^A-Za-z0-9-]/g, '-');
  }

  function c1SpatialPayload(root, intake) {
    var locale = clean(document.documentElement && document.documentElement.lang) || 'zh-HK';
    var qualifiers = [];
    if (intake.property_type) qualifiers.push('property type ' + intake.property_type);
    if (intake.intent) qualifiers.push('valuation purpose ' + intake.intent);
    return {
      request_id: c1RequestId(),
      contract: clean(root.dataset.spatialContract) || C1_SPATIAL_CONTRACT,
      evidence_mode: 'include',
      input: 'Check valuation preparation and market context for ' + intake.location + (qualifiers.length ? '; ' + qualifiers.join('; ') : ''),
      locale: locale,
      surface: 'valuation_precheck',
      consumer_job: clean(root.dataset.consumerJob) || C1_CONSUMER_JOB,
      property_type: intake.property_type,
      place_context: {
        state: 'single',
        items: [{ text: intake.location, state: 'explicit', source: 'c1_free_precheck' }]
      }
    };
  }

  function c1FetchSpatial(root, intake) {
    var endpoint = clean(root.dataset.spatialEndpoint) || 'https://api.glhksolution.com/api/spatial/entry';
    var controller = new window.AbortController();
    var timer = window.setTimeout(function () { controller.abort(); }, 20000);
    return window.fetch(endpoint, {
      method: 'POST',
      credentials: 'omit',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(c1SpatialPayload(root, intake))
    }).then(function (response) {
      return response.text().then(function (text) {
        var payload = null;
        try { payload = text ? JSON.parse(text) : null; } catch (_) {}
        if (!response.ok || !payload || payload.ok !== true || payload.success === false || payload.contract !== C1_SPATIAL_CONTRACT) {
          throw new Error('spatial_precheck_unavailable');
        }
        return payload;
      });
    }).catch(function (error) {
      if (controller.signal.aborted) throw new Error('spatial_precheck_timeout');
      throw error;
    }).finally(function () { window.clearTimeout(timer); });
  }

  function c1FillList(node, items, emptyText) {
    if (!node) return;
    node.textContent = '';
    if (!items.length) {
      node.appendChild(make('li', {}, emptyText));
      return;
    }
    items.forEach(function (item) { node.appendChild(make('li', {}, item.label || clean(item))); });
  }

  function c1PlaceLabel(place, fallback) {
    return clean(place && (place.label || place.text)) || clean(fallback);
  }

  function c1KnownItems(intake, place, placeVerified) {
    var known = [];
    var placeLabel = c1PlaceLabel(place, intake.location);
    if (placeVerified) {
      known.push({ label: 'Building identity：已核實到唯一物業' });
      known.push({ label: 'Address：' + placeLabel });
      if (Number.isFinite(Number(place.lat)) && Number.isFinite(Number(place.lng))) {
        known.push({ label: '位置座標：' + Number(place.lat).toFixed(5) + ', ' + Number(place.lng).toFixed(5) });
      }
      if (clean(place.source)) known.push({ label: '位置資料來源：' + clean(place.source) });
    }
    if (intake.property_type) known.push({ label: 'Property type：' + (C1_PROPERTY_TYPE_LABELS[intake.property_type] || intake.property_type) });
    if (intake.intent) known.push({ label: '估價用途：' + (C1_INTENT_LABELS[intake.intent] || intake.intent) });
    (intake.available_information || []).slice(0, 6).forEach(function (key) {
      known.push({ label: '已有：' + (C1_INFORMATION_LABELS[key] || key) });
    });
    return known;
  }

  function c1RuleResults(intake, readiness, placeVerified) {
    function state(hasValue, failWhenMissing) { return hasValue ? 'pass' : (failWhenMissing ? 'fail' : 'warning'); }
    var rules = [
      { label: '物業位置可唯一核實', status: state(placeVerified, true) },
      { label: '權屬 / 業權資料已確認', status: state(readiness.available.has('ownership'), false) },
      { label: '樓面面積來源已確認', status: state(readiness.available.has('area_survey'), false) },
      { label: '樓層 / 單位已確認', status: state(readiness.available.has('floor_confirmation'), false) },
      { label: '現時租用 / 空置狀態已確認', status: state(readiness.available.has('tenancy_confirmation'), false) }
    ];
    if (['industrial', 'retail', 'office'].indexOf(intake.property_type) !== -1) {
      rules.push({ label: '消防 / 通風 / 排煙資料未在免費預檢中核實，正式用途或改裝判斷需另行專業核查', status: 'warning' });
    }
    return rules;
  }

  function c1RenderRules(root, rules) {
    var list = root.querySelector('[data-c1-rule-list]');
    if (!list) return;
    list.textContent = '';
    rules.forEach(function (rule) {
      var item = make('li', { 'data-rule-status': rule.status });
      item.appendChild(make('span', { className: 'product-action-panel__status', 'data-status': rule.status }, rule.status.toUpperCase()));
      item.appendChild(make('span', {}, rule.label));
      list.appendChild(item);
    });
  }

  function c1NextSteps(intake, readiness, placeVerified) {
    var steps = [];
    if (!placeVerified) steps.push('補充大廈名稱、街道門牌，以及適用時的樓層 / 單位，再重新核實唯一物業。');
    if (!readiness.available.has('ownership')) steps.push('取得或核對最新權屬 / 業權資料，記錄資料來源與更新日期。');
    if (!readiness.available.has('area_survey')) steps.push('確認樓面面積數字、量度口徑、資料來源及 as_of 日期；不要只保留未註明來源的面積。');
    if (!readiness.available.has('floor_confirmation')) steps.push('以圖則、樓層資料或現場核對確認樓層 / 單位，避免只憑地址文字推定。');
    if (!readiness.available.has('tenancy_confirmation')) steps.push('確認目前自用、出租或空置狀態；如出租，準備現行租約或可核實的租用資料。');
    if (steps.length < 5 && ['industrial', 'retail', 'office'].indexOf(intake.property_type) !== -1) {
      steps.push('如用途、改裝或正式評估範圍相關，現場核對當前用途及適用的消防、通風 / 排煙等資料，再交由相應專業人士判斷。');
    }
    if (steps.length < 3) steps.push('保存本次引用的公開資料來源與資料期別，之後更新時可分辨市場背景是否已過期。');
    if (steps.length < 3) steps.push('正式估價前，把實際用途、委託用途與所需估價日期交給估價師確認專業範圍。');
    if (steps.length < 3) steps.push('在現場或最新照片中再確認物業當前實際狀況，避免只依賴舊文件。');
    return steps.slice(0, 5);
  }

  function c1RenderNext(root, steps) {
    var list = root.querySelector('[data-c1-next-list]');
    if (!list) return;
    list.textContent = '';
    steps.forEach(function (step) { list.appendChild(make('li', {}, step)); });
  }

  function c1RenderResult(root, intake, payload) {
    var data = payload && payload.data && typeof payload.data === 'object' ? payload.data : payload || {};
    var result = root.querySelector('[data-c1-result]');
    var form = root.querySelector('[data-c1-precheck-form]');
    if (!result) return;

    var place = data.place && typeof data.place === 'object' ? data.place : {};
    var placeVerified = place.verified === true;
    var placeLabel = c1PlaceLabel(place, intake.location);
    var readiness = c1Readiness(intake, placeVerified);
    var title = root.querySelector('[data-c1-result-title]');
    var lead = root.querySelector('[data-c1-result-lead]');
    var placeNode = root.querySelector('[data-c1-place]');
    var readinessLabel = root.querySelector('[data-c1-readiness-label]');
    var readinessMeter = root.querySelector('[data-c1-readiness-meter]');

    if (title) title.textContent = placeVerified ? (readiness.missing.length ? '建議再準備 ' + readiness.missing.length + ' 項資料' : '基本資料已較完整') : '需要更具體的物業地址';
    if (lead) lead.textContent = placeVerified ? (readiness.missing.length ? '補充後，更方便估價師進行初步審核。' : '你已提供常用的基本資料，仍可按實際估價用途補充其他證據。') : '目前未能唯一核實物業；這屬資料入口 Fail，不代表物業或估價本身不合格。';
    if (placeNode) placeNode.textContent = placeVerified ? '已核實物業：' + placeLabel : '物業位置尚未唯一核實。';
    if (readinessLabel) readinessLabel.textContent = 'Ready ' + readiness.score + '%';
    if (readinessMeter) {
      readinessMeter.value = readiness.score;
      readinessMeter.setAttribute('aria-valuenow', String(readiness.score));
    }

    c1FillList(root.querySelector('[data-c1-missing-list]'), readiness.missing, '暫未發現這三項常用資料待完善');
    c1FillList(root.querySelector('[data-c1-ready-list]'), readiness.ready, '你暫未勾選其他已有資料');
    c1FillList(root.querySelector('[data-c1-known-list]'), c1KnownItems(intake, place, placeVerified), '目前只有地址輸入，尚未形成可核實 Known Block');
    c1RenderRules(root, c1RuleResults(intake, readiness, placeVerified));
    c1RenderNext(root, c1NextSteps(intake, readiness, placeVerified));

    var market = data.structured_result && data.structured_result.market_context;
    var marketNode = root.querySelector('[data-c1-market]');
    var latest = market && Array.isArray(market.items) && market.items[0];
    var sourceUrl = null;
    try { sourceUrl = latest && new URL(latest.source_url); } catch (_) {}
    var marketSafe = !!(market && market.market_context_ready === true && market.valuation_ready === false && market.opinion_of_value === false &&
      latest && latest.value !== null && clean(latest.value) !== '' && Number.isFinite(Number(latest.value)) &&
      clean(latest.source_id) && clean(latest.provider_name) && clean(latest.period) && sourceUrl &&
      sourceUrl.protocol === 'https:' && /(^|\.)gov\.hk$/i.test(sourceUrl.hostname));
    var marketUnavailable = root.querySelector('[data-c1-market-unavailable]');
    if (marketUnavailable) marketUnavailable.hidden = marketSafe;
    if (marketNode) marketNode.hidden = !marketSafe;
    if (marketSafe) {
      var items = Array.isArray(market.items) ? market.items : [];
      latest = items[0];
      var metricName = clean(latest.metric) === 'price_index' ? '私人住宅價格指數' : clean(latest.metric).replace(/_/g, ' ') || '市場指標';
      var valueNode = root.querySelector('[data-c1-market-value]');
      var labelNode = root.querySelector('[data-c1-market-label]');
      var periodNode = root.querySelector('[data-c1-market-period]');
      var sourceNode = root.querySelector('[data-c1-market-source]');
      var providerName = clean(latest.provider_name || market.sources && market.sources[0] && market.sources[0].provider) || '官方公開資料';
      if (valueNode) valueNode.textContent = Number.isFinite(Number(latest.value)) ? String(latest.value) : '—';
      if (labelNode) labelNode.textContent = metricName;
      if (periodNode) periodNode.textContent = clean(market.latest_period || latest.period) || '—';
      if (sourceNode) { sourceNode.textContent = providerName; sourceNode.href = sourceUrl.href; }
      if (marketNode) marketNode.setAttribute('data-c1-market-provider', /\bRVD\b|Rating and Valuation Department|差餉物業估價署/i.test(providerName) ? 'rvd' : 'official');
    }

    root.setAttribute('data-c1-readiness-score', String(readiness.score));
    root.setAttribute('data-c1-place-verification', placeVerified ? 'pass' : 'fail');
    if (form) form.hidden = true;
    result.hidden = false;
    try { result.focus({ preventScroll: true }); } catch (_) { result.focus(); }
    result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  function bindPublicPropertyRead(root) {
    var kind = root.getAttribute('data-gproperty-public-read');
    if (!kind) return false;
    var allowed = {
      building: 'glg-land-intelligence-tool', market: 'glg-property-investment-intelligence'
    };
    if (!hasOwn(allowed, kind) || allowed[kind] !== root.dataset.productHandle) return true;
    var locale = root.dataset.locale;
    var simplified = locale === 'zh-CN';
    var zh = /^zh/.test(locale);
    var copy = simplified
      ? ['请先在 GProperty 搜索并确认地址。', '正在读取官方市场资料…', '市场资料暂未就绪，请稍后重试。', '来源、再使用授权、日期或临时数字标记尚未完整核实，暂不显示指数。', 'P 代表临时数字；市场资料不是此物业的估值或投资建议。', '来源更新日期', '提取日期', '未提供']
      : zh
        ? ['請先在 GProperty 搜尋並確認地址。', '正在讀取官方市場資料…', '市場資料暫未就緒，請稍後重試。', '來源、再使用授權、日期或臨時數字標記尚未完整核實，暫不顯示指數。', 'P 代表臨時數字；市場資料不是此物業的估值或投資建議。', '來源更新日期', '擷取日期', '未提供']
        : ['Search and confirm an address in GProperty first.', 'Reading official market data…', 'Market data is unavailable. Please retry later.', 'Source, reuse permission, dates or provisional flags still need verification. Index values are withheld.', 'P means provisional. Market context is not a valuation of this property or investment advice.', 'Source update date', 'Retrieved', 'Not supplied'];
    var gate = window.GPROPERTY_DATA_GATE;
    var stored = gate && gate.readPropertyDraft(gate.canonicalDraftKey, gate.legacyDraftKey);
    var draft = stored && stored.draft;
    var requested = new URL(window.location.href).searchParams.get('record_id');
    var verified = gate && gate.isVerifiedDraft(draft) && (!requested || gate.opaque(requested) === draft.record_id);
    root.querySelector('[data-public-address]').textContent = verified ? gate.locationLabel(draft.estate_context) : copy[0];
    var control = root.querySelector('[data-public-market-read]');
    if (!control) return true;
    control.addEventListener('click', async function () {
      if (control.disabled) return;
      var output = resultNode(root);
      output.hidden = true;
      output.replaceChildren();
      var api = window.GCityExecutor;
      if (!api || typeof api.readMarketContext !== 'function') return setState(root, control, 'blocked', copy[2]);
      setState(root, control, 'executing', copy[1]);
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = window.setTimeout(function () { if (controller) controller.abort(); }, 12000);
      try {
   const response = await api.readMarketContext({
          signal: controller && controller.signal,
          productHandle: root.dataset.productHandle
        });        var body = response && response.data;
        if (!response || response.ok !== true || response.success === false || !body || body.ok !== true || body.market_context_ready !== true || body.valuation_ready !== false || body.opinion_of_value !== false || body.mock_data_used === true) throw new Error('market_unavailable');
       const marketRows = Array.isArray(body.items) ? body.items : [];
        if (marketRows.length < 2 || marketRows.length > 3) {
          throw new Error('market_periods_unavailable');
        }

        const validRows = marketRows.every(function (row) {
          try {
            if (!row || typeof row !== 'object') return false;

            const sourceUrl = new URL(row.source_url);
            const numericValue =
              typeof row.value === 'number' ||
              (
                typeof row.value === 'string' &&
                row.value.trim() !== ''
              );

            return (
              numericValue &&
              Number.isFinite(Number(row.value)) &&
              Number(row.value) >= 0 &&
              row.metric === 'price_index' &&
              row.property_category === 'private_domestic' &&
              row.property_class === 'All Classes' &&
              row.geography_type === 'territory-wide' &&
              row.geography_code === 'HK' &&
              row.unit === 'index_1999_100' &&
              row.source_id === 'rvd-private-domestic-price-index-monthly' &&
              typeof row.version_id === 'string' &&
              row.version_id.trim() !== '' &&
              /^\d{4}-(0[1-9]|1[0-2])$/.test(row.period) &&
              sourceUrl.protocol === 'https:' &&
              sourceUrl.hostname === 'www.rvd.gov.hk' &&
              sourceUrl.pathname === '/datagovhk/1.4M.csv' &&
              !sourceUrl.username &&
              !sourceUrl.password &&
              !sourceUrl.port &&
              !sourceUrl.search &&
              !sourceUrl.hash
            );
          } catch (_) {
            return false;
          }
        });

        if (!validRows) throw new Error('market_source_unverified');

        const rows = marketRows.slice().sort(function (a, b) {
          return b.period.localeCompare(a.period);
        });

        const monthNumber = function (period) {
          const parts = period.split('-').map(Number);
          return parts[0] * 12 + parts[1] - 1;
        };

        const compatiblePeriods = rows.every(function (row, index) {
          if (index === 0) return true;
          const previousRow = rows[index - 1];
          return (
            row.source_id === rows[0].source_id &&
            row.version_id === rows[0].version_id &&
            monthNumber(previousRow.period) - monthNumber(row.period) === 1
          );
        });

        if (!compatiblePeriods) {
          throw new Error('market_series_mismatch');
        }
        // P03 interim release gate, pinned to the RVD CSV verified on 2026-11-02.
        // Raw CSV SHA256: 6344bd9f5fe79897ff26fc4cc33b0a7ab57ceb6dad8e01e022453216c7f9a6f4
        // All Classes / All Classes - Remarks: 07/08/09-2026 = 320.1/320.2/320.8, all P.
        // The API does not yet expose monthly remarks. Never infer remarks for a new version.
        // #933 must own generalised remarks/freshness for later source versions.
        var verifiedRvdVersion = 'v_6a009b72a3c2d3695fcd';
        var verifiedRvdRows = {
          '2026-07': 320.1,
          '2026-08': 320.2,
          '2026-09': 320.8
        };
        var resourceUpdatedAt = '2026-10-28';
        var resourceAgeMs = Date.now() - Date.parse(resourceUpdatedAt + 'T00:00:00Z');
        var sourceCurrent = resourceAgeMs >= 0 && resourceAgeMs <= 42 * 86400000;
        var snapshotVerified = sourceCurrent && rows[0].period === '2026-09' && rows.every(function (row) {
          return row.version_id === verifiedRvdVersion &&
            Object.prototype.hasOwnProperty.call(verifiedRvdRows, row.period) &&
            Math.abs(Number(row.value) - verifiedRvdRows[row.period]) < 0.000001 &&
            (row.remarks == null || String(row.remarks).trim() === 'P') &&
            row.is_provisional !== false && row.provisional !== false;
        });
        if (!snapshotVerified) {
          output.appendChild(make('p', {}, copy[3]));
          output.hidden = false;
          output.focus({ preventScroll: true });
          setState(root, control, 'data-pending', copy[3]);
          return;
        }
        var latest = rows[0];
        var previous = rows[1];
        var latestValue = Number(latest.value);
        var previousValue = Number(previous.value);
        if (!(previousValue > 0)) throw new Error('market_comparison_unavailable');
        var change = latestValue - previousValue;
        var percent = change / previousValue * 100;
        var signed = function (number, decimals) {
          return (number > 0 ? '+' : '') + number.toFixed(decimals);
        };
        output.appendChild(make('strong', {}, (zh ? '全港私人住宅售價指數：' : 'Hong Kong private domestic price index: ') + latestValue.toFixed(1)));
        output.appendChild(make('p', {}, latest.period + ' · All Classes · 1999 = 100'));
        output.appendChild(make('p', {}, (zh ? '按月變動：' : 'Monthly change: ') + signed(change, 1) + (zh ? ' 指數點（' : ' points (') + signed(percent, 2) + '%)'));
        output.appendChild(make('p', {}, (zh ? '所顯示月份均為 P 暫定數字，可能修訂。' : 'All displayed months are P (provisional) and subject to revision.')));
        output.appendChild(make('a', { href: latest.source_url, rel: 'noopener noreferrer' }, clean(latest.provider_name || latest.dataset_name || latest.source_id)));
        output.appendChild(make('p', {}, (zh ? 'DATA.GOV.HK 資源頁更新：' : 'DATA.GOV.HK resource updated: ') + resourceUpdatedAt + (zh ? '；並非 API source_updated_at。' : '; not the API source_updated_at.')));
        output.appendChild(make('p', {}, copy[6] + ': ' + (clean(latest.retrieved_at) || copy[7])));
        output.appendChild(make('a', { href: 'https://data.gov.hk/tc/terms-and-conditions', rel: 'noopener noreferrer' }, (zh ? '資料使用條款及權利歸屬：香港特區政府／差餉物業估價署' : 'Data terms and attribution: HKSAR Government / Rating and Valuation Department')));
        output.appendChild(make('p', {}, copy[4]));
        output.hidden = false;
        output.focus({ preventScroll: true });
        setState(root, control, 'ready', zh ? '官方市場資料已載入（P 暫定數字）。' : 'Official market data loaded (provisional).');
      } catch (_) {
        setState(root, control, 'failed', copy[2]);
      } finally {
        window.clearTimeout(timer);
      }
    });
    return true;
  }

  function bindC1Precheck(root) {
    if (!root || !root.matches('[data-c1-property-precheck]')) return false;
    if (root.dataset.c1Runtime === 'ready') return true;
    root.dataset.c1Runtime = 'ready';

    var form = root.querySelector('[data-c1-precheck-form]');
    var submit = root.querySelector('[data-c1-free-submit]');
    var reset = root.querySelector('[data-c1-reset]');
    var result = root.querySelector('[data-c1-result]');
    var requiredResultSelectors = [
      '[data-c1-readiness-label]', '[data-c1-readiness-meter]', '[data-c1-known-list]', '[data-c1-missing-list]',
      '[data-c1-ready-list]', '[data-c1-rule-list]', '[data-c1-next-list]', '[data-c1-market]'
    ];
    if (!form || !submit || !result || requiredResultSelectors.some(function (selector) { return !root.querySelector(selector); })) {
      setState(root, submit, 'failed', '免費快檢介面未完整載入。');
      return true;
    }

    c1BindAddressPrompt(root);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submit.disabled || submit.getAttribute('aria-busy') === 'true') return;
      var intake = validateC1Intake(root, submit);
      if (!intake) return;

      setState(root, submit, 'executing', '正在核實物業並讀取官方市場背景…');
      c1FetchSpatial(root, intake).then(function (payload) {
        c1RenderResult(root, intake, payload);
        setState(root, submit, 'complete', '免費檢查完成。');
      }).catch(function (error) {
        var message = clean(error && error.message);
        if (/unsupported_contract|invalid_input/i.test(message)) message = '目前未能完成資料檢查，請稍後再試。';
        else if (/timeout|timed out/i.test(message)) message = '查詢逾時，沒有建立任何付費或交付工作。請再試一次。';
        else message = '目前未能取得完整公開資料，沒有建立任何付費或交付工作。請按「免費檢查」重試。';
        setState(root, submit, 'failed', message);
      });
    });

    if (reset) {
      reset.addEventListener('click', function () {
        result.hidden = true;
        form.hidden = false;
        setState(root, submit, 'idle', '');
        var location = root.querySelector('[data-c1-location]');
        if (location) location.focus();
      });
    }

    return true;
  }

  function genericContext(root) {
    return {
      delivery_type: clean(root.dataset.deliveryType), delivery_method: clean(root.dataset.deliveryMethod), engine_type: clean(root.dataset.engineType),
      default_scene: clean(root.dataset.productHandle || 'product'), api_family: clean(root.dataset.apiFamily), canonical_endpoint: clean(root.dataset.canonicalEndpoint),
      api_trigger_enabled: root.dataset.runnable === 'true', result_surface: clean(root.dataset.resultSurface || 'panel'),
      requires_entitlement: bool(root.dataset.requiresEntitlement), requires_gfun: bool(root.dataset.requiresGfun),
      data: { product_id: clean(root.dataset.productId), product_handle: clean(root.dataset.productHandle), source: 'product-action-panel' }
    };
  }
  function bindGeneric(root, control) {
    if (!control || root.dataset.runnable !== 'true') return;
    control.addEventListener('click', function (event) {
      if (event.defaultPrevented || control.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      if (!executorReady()) return setState(root, control, 'blocked', 'The product execution service is not ready. No request was submitted.');
      busy(root, control, function () { return window.GCityExecutor.execute(genericContext(root)); }).catch(function () {});
    });
  }
  function bind(root) {
    if (!root || root.dataset.productActionRuntime === 'ready') return;
    root.dataset.productActionRuntime = 'ready';
    if (bindPublicPropertyRead(root)) return;
    var locked = lockedConfig(root);
    var generic = root.querySelector(ACTION_SELECTOR);
    if (bindC1Precheck(root)) return;
    preparePaidTutoringShell(root);
    bindPaidToolPurchase(root);
    if (!locked) return bindGeneric(root, generic);
    if (clean(root.dataset.productId) && clean(root.dataset.productId) !== locked.id) {
      return setState(root, generic, 'blocked', 'Product identity mismatch. No request was submitted.');
    }
    if (generic) generic.hidden = true;
    if (locked.role === 'voice') bindVoice(root);
    else if (locked.role === 'memory') bindMemory(root);
    else if (locked.role === 'tutoring') bindTutoring(root);
    else if (locked.role === 'rights') bindRights(root);
  }

  /* C1-LANDING-V2-CTA-01: existing primary anchor reveals the existing inline form. */
  function bindC1LandingEntry() {
    if (document.documentElement.dataset.c1LandingEntryBound === 'true') return;
    document.documentElement.dataset.c1LandingEntryBound = 'true';
    document.addEventListener('click', function (event) {
      var target = event.target && event.target.nodeType === 1 ? event.target : event.target && event.target.parentElement;
      var link = target && target.closest && target.closest('.main-product-native--c1-free .main-product-native__btn--primary[href="#product-action"]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      var panel = document.querySelector('[data-module="product-action-panel"][data-c1-property-precheck]');
      var form = panel && panel.querySelector('[data-c1-precheck-form]');
      var address = form && form.querySelector('[data-c1-location]');
      if (!panel || !form || !address) return;
      event.preventDefault();
      form.hidden = false;
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      try { address.focus({ preventScroll: true }); } catch (_error) { address.focus(); }
    });
  }

  function boot(scope) { Array.prototype.slice.call((scope || document).querySelectorAll(ROOT_SELECTOR)).forEach(bind); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); bindC1LandingEntry(); }, { once: true });
  else { boot(document); bindC1LandingEntry(); }
  document.addEventListener('shopify:section:load', function (event) { boot(event.target || document); });
})(window, document);
